import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { X, Send, Paperclip, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Send Custom Email modal — attached to a Loan (also usable on Application/Firm).
 * Supports:
 *  - Recipient pre-population + free-form entry
 *  - CC/BCC
 *  - Multi-attachment picker (payoffs, contracts, firm statements, uploads)
 *  - Optional Save-to-Contact
 */
export default function SendCustomEmailDialog({
  open,
  onClose,
  loanId,
  applicationId,
  firmId,
  defaultRecipient,
  defaultAttorneyName,
  onSent,
}) {
  const [recipient, setRecipient] = useState(defaultRecipient || "");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saveToContact, setSaveToContact] = useState(false);
  const [saveAsName, setSaveAsName] = useState(defaultAttorneyName || "");
  const [saveAsRole, setSaveAsRole] = useState("attorney");
  const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);
  const [availableAttachments, setAvailableAttachments] = useState([]);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setRecipient(defaultRecipient || "");
      setSaveAsName(defaultAttorneyName || "");
      setCc(""); setBcc(""); setSubject(""); setBody("");
      setSaveToContact(false); setSelectedAttachments([]);
      // Fetch attachable documents for this loan
      if (loanId) {
        const params = { loan_id: loanId };
        api.get("/comms/attachable-documents", { params })
          .then(r => setAvailableAttachments(r.data || []))
          .catch(() => setAvailableAttachments([]));
      }
    }
  }, [open, loanId, defaultRecipient, defaultAttorneyName]);

  const toggleAttachment = (a) => {
    setSelectedAttachments(prev => {
      const key = `${a.source}:${a.ref_id}`;
      const exists = prev.some(x => `${x.source}:${x.ref_id}` === key);
      return exists ? prev.filter(x => `${x.source}:${x.ref_id}` !== key) : [...prev, a];
    });
  };

  const send = async () => {
    if (!recipient || !subject || !body) {
      toast.error("Recipient, subject, and body are required.");
      return;
    }
    setSending(true);
    try {
      const payload = {
        recipient,
        cc: cc.split(",").map(e => e.trim()).filter(Boolean),
        bcc: bcc.split(",").map(e => e.trim()).filter(Boolean),
        subject,
        body_html: body.startsWith("<") ? body : `<p style="margin:0 0 16px 0;">${body.replace(/\n\n/g, '</p><p style="margin:0 0 16px 0;">').replace(/\n/g, "<br/>")}</p>`,
        loan_id: loanId,
        application_id: applicationId,
        firm_id: firmId,
        save_to_contact: saveToContact,
        save_as_name: saveAsName,
        save_as_role: saveAsRole,
        attachments: selectedAttachments,
      };
      const { data } = await api.post("/comms/send-custom", payload);
      if (data.status === "sent") toast.success("Email sent");
      else if (data.status === "skipped") toast.warning(`Skipped: ${data.reason} (RESEND_API_KEY not configured yet)`);
      else if (data.status === "suppressed") toast.error(`Blocked: recipient is suppressed`);
      else toast.error(`Failed: ${data.error || "unknown"}`);
      onSent?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose} data-testid="send-custom-email-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Send className="w-4 h-4" /> Send email</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          <Field label="To">
            <input type="email" value={recipient} onChange={e => setRecipient(e.target.value)}
              placeholder="attorney@examplelaw.com"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="custom-email-to" />
          </Field>
          <Field label="Cc">
            <input value={cc} onChange={e => setCc(e.target.value)} placeholder="comma-separated"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Bcc">
            <input value={bcc} onChange={e => setBcc(e.target.value)} placeholder="comma-separated"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Subject">
            <input value={subject} onChange={e => setSubject(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="custom-email-subject" />
          </Field>
          <Field label="Message">
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={8}
              placeholder="Write your message… (line breaks are preserved)"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="custom-email-body" />
          </Field>

          {/* Attachments */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Attachments</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedAttachments.map(a => (
                <span key={`${a.source}:${a.ref_id}`} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
                  <Paperclip className="w-3 h-3" />{a.filename}
                  <button onClick={() => toggleAttachment(a)} className="ml-1 text-slate-500 hover:text-red-600">×</button>
                </span>
              ))}
            </div>
            <button onClick={() => setAttachmentPickerOpen(v => !v)}
              className="text-xs text-[#0066FF] hover:text-[#0055D4] inline-flex items-center gap-1"
              data-testid="attachment-picker-toggle">
              <Paperclip className="w-3.5 h-3.5" /> {attachmentPickerOpen ? "Hide" : "Add"} attachments ({availableAttachments.length} available)
            </button>
            {attachmentPickerOpen && (
              <div className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
                {availableAttachments.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 italic">No documents available on this loan yet.</div>
                ) : availableAttachments.map(a => {
                  const selected = selectedAttachments.some(x => x.source === a.source && x.ref_id === a.ref_id);
                  return (
                    <button key={`${a.source}:${a.ref_id}`} onClick={() => toggleAttachment(a)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 ${selected ? "bg-blue-50" : ""}`}>
                      <input type="checkbox" checked={selected} readOnly className="pointer-events-none" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900 truncate">{a.filename}</div>
                        <div className="text-xs text-slate-500">{a.source} · {a.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Save to contact */}
          <div className="border-t border-slate-200 pt-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={saveToContact} onChange={e => setSaveToContact(e.target.checked)}
                data-testid="save-to-contact-checkbox" />
              Save <code className="text-xs">{recipient || "recipient"}</code> to firm contacts
            </label>
            {saveToContact && (
              <div className="grid grid-cols-2 gap-3 mt-2 ml-6">
                <input value={saveAsName} onChange={e => setSaveAsName(e.target.value)} placeholder="Contact name"
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
                <select value={saveAsRole} onChange={e => setSaveAsRole(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
                  <option value="attorney">Attorney</option>
                  <option value="paralegal">Paralegal</option>
                  <option value="case_manager">Case Manager</option>
                </select>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={send} disabled={sending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0066FF] text-white text-sm font-medium rounded-lg hover:bg-[#0055D4] disabled:opacity-50"
            data-testid="custom-email-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
